/** Read-only projection for legacy visualizers: source snapshots are never mutated.
 * Keep this adapter aligned with the backend MeasurementNormalizer (parity tests). */
import { convertMeasurement, parseMeasurementUnit, type MeasurementUnit } from './measurement'
import type { OptimizationRun } from '../types/optimization'

function convert(value: any, _kind: string, from: any, to: MeasurementUnit, _field?: string): number {
  return convertMeasurement(Number(value), parseMeasurementUnit(from), to)
}
function unitCode(_kind: string, value: any, _field?: string) { return parseMeasurementUnit(value) }


// Every field family has its own source unit. Nested physics/pallet settings
// default to their historical mm/kg contract, not to the enclosing product unit.
function fields(source: any, keys: string[], kind: string, unitKey: string, target: MeasurementUnit, fallback: MeasurementUnit = target): any {
  const result = { ...source };
  const from = source[unitKey] ?? fallback;
  unitCode(kind, from, unitKey);
  for (const key of keys) {
    const specific = `${key}_unit`;
    if (source[specific] != null) unitCode(kind, source[specific], specific);
    if (source[key] != null) result[key] = convert(source[key], kind, source[specific] ?? from, target, key);
    if (source[specific] != null) result[specific] = target;
  }
  result[unitKey] = target;
  return result;
}

function dimensions(source: any = {}, prefixes = ['']): any {
  let result = fields(source, ['length', 'width', 'height', 'internal_length', 'internal_width', 'internal_height', 'cargo_length', 'cargo_width', 'cargo_height', 'x', 'y', 'z'], 'length', 'dimension_unit', 'mm');
  for (const axis of ['length', 'width', 'height']) {
    if (source[axis] != null) continue;
    for (const prefix of prefixes) {
      if (source[`${prefix}${axis}_mm`] != null) {
        result[axis] = convert(source[`${prefix}${axis}_mm`], 'length', 'mm', 'mm', `${prefix}${axis}_mm`); break;
      }
      if (source[`${prefix}${axis}_cm`] != null) {
        result[axis] = convert(source[`${prefix}${axis}_cm`], 'length', 'cm', 'mm', `${prefix}${axis}_cm`); break;
      }
      if (prefix && source[`${prefix}${axis}`] != null) {
        result[axis] = convert(source[`${prefix}${axis}`], 'length', source.dimension_unit ?? 'mm', 'mm', `${prefix}${axis}`); break;
      }
    }
  }
  return result;
}

function constraints(source: any = {}): any {
  let result = fields(source, ['max_weight', 'maxWeight', 'maxPayload', 'max_payload'], 'mass', 'weight_unit', 'kg');
  result = fields(result, ['max_stack_height', 'maxStackHeight', 'pressure_cell_target_size', 'pressureCellTargetSize'], 'length', 'dimension_unit', 'mm');
  result = fields(result, ['max_stack_weight', 'maxStackWeight'], 'mass', 'stack_weight_unit', 'kg');
  result = fields(result, ['maxFloorPressure', 'max_floor_pressure'], 'surfaceLoad', 'floor_pressure_unit', 'kg/m2');
  for (const key of ['payload_capacity_kg', 'payloadCapacityKg']) {
    if (source[key] != null) result[key] = convert(source[key], 'mass', 'kg', 'kg', key);
  }
  if (source.weight && typeof source.weight === 'object') result.weight = constraints(source.weight);
  if (source.floorPressure) result.floorPressure = fields(source.floorPressure, ['maxPressure'], 'surfaceLoad', 'unit', 'kg/m2', source.floor_pressure_unit ?? 'kg/m2');
  return result;
}

function axleConfig(source: any): any {
  if (!source) return source;
  if (typeof source === 'string') {
    try { source = JSON.parse(source); } catch (_) { return source; }
  }
  let result = fields(source, ['position', 'centerPosition', 'trackWidth', 'kingPinPosition', 'fifthWheelPosition'], 'length', 'dimension_unit', 'mm');
  result = fields(result, ['maxWeight', 'emptyWeight', 'empty_weight', 'fifthWheelMaxLoad'], 'mass', 'weight_unit', 'kg');
  for (const key of ['axles', 'axleGroups']) {
    if (Array.isArray(source[key])) result[key] = source[key].map((entry: any) => axleConfig({
      dimension_unit: source.dimension_unit ?? 'mm', weight_unit: source.weight_unit ?? 'kg', ...entry
    }));
  }
  return result;
}

function obstacle(source: any): any {
  let result = dimensions(source);
  if (source.position) result.position = dimensions({ dimension_unit: source.dimension_unit ?? 'mm', ...source.position });
  if (source.dimensions) result.dimensions = dimensions({ dimension_unit: source.dimension_unit ?? 'mm', ...source.dimensions });
  return result;
}

function space(source: any = {}): any {
  let result = dimensions(source, ['', 'internal_', 'cargo_']);
  for (const key of ['payload_capacity_kg', 'payloadCapacityKg']) {
    if (source[key] != null) result[key] = convert(source[key], 'mass', 'kg', 'kg', key);
  }
  result = fields(result, ['weight', 'tare_weight', 'net_weight', 'gross_weight', 'max_weight', 'maxWeight', 'capacity_weight', 'empty_weight', 'max_weight_permitted', 'emptyWeight', 'maxTotalWeight'], 'mass', 'weight_unit', 'kg');
  result = fields(result, ['volume', 'max_volume', 'capacity_volume'], 'volume', 'volume_unit', 'mm3', 'm3');
  // Old generated load spaces used an unlabeled volume in cm3. Do not guess
  // its scale: the engine's geometric capacity is defined by normalized edges.
  if (source.max_volume != null && source.volume_unit == null && source.max_volume_unit == null &&
      result.length != null && result.width != null && result.height != null) {
    result.max_volume = result.length * result.width * result.height;
  }
  result = fields(result, ['maxFloorPressure', 'max_floor_pressure'], 'surfaceLoad', 'floor_pressure_unit', 'kg/m2');
  if (source.axle_configuration) result.axle_configuration = axleConfig(source.axle_configuration);
  if (source.axle_rules) result.axle_rules = axleConfig(source.axle_rules);
  if (Array.isArray(source.obstacles)) result.obstacles = source.obstacles.filter(Boolean).map(obstacle);
  return result;
}

function item(source: any = {}): any {
  let result = dimensions(source, ['', 'product_']);
  result = fields(result, ['weight', 'tare_weight', 'net_weight', 'gross_weight'], 'mass', 'weight_unit', 'kg');
  if (source.weight == null) result.weight = convert(source.weight_kg ?? source.gross_weight_kg ?? 0, 'mass', 'kg', 'kg', 'weight_kg');
  result = fields(result, ['volume'], 'volume', 'volume_unit', 'mm3', 'm3');
  result = fields(result, ['max_stack_weight', 'maxStackWeight'], 'mass', 'stack_weight_unit', 'kg');
  result = fields(result, ['max_stack_height', 'maxStackHeight'], 'length', 'stack_height_unit', 'mm');
  result = fields(result, ['pallet_custom_length', 'pallet_custom_width'], 'length', 'pallet_dimension_unit', 'mm');
  // Explicitly suffixed fields retain their units, regardless of the display/source profile.
  for (const key of ['max_pallet_height_mm', 'pallet_board_height_mm']) {
    if (source[key] != null) result[key] = convert(source[key], 'length', 'mm', 'mm', key);
  }
  for (const key of ['pallet_empty_weight_kg', 'max_pallet_weight_kg']) {
    if (source[key] != null) result[key] = convert(source[key], 'mass', 'kg', 'kg', key);
  }
  if (source.constraints) result.constraints = constraints(source.constraints);
  return result;
}

function combination(source: any): any {
  if (!source?.coupling) return source;
  let coupling = fields(source.coupling, ['trailerKingPinPosition', 'tractorFifthWheelPosition'], 'length', 'dimension_unit', 'mm');
  coupling = fields(coupling, ['fifthWheelMaxLoad'], 'mass', 'weight_unit', 'kg');
  return { ...source, coupling };
}

function request(source: any = {}): any {
  const result = { ...source };
  for (const key of ['vehicle', 'tractor', 'transportUnit', 'loadSpace']) if (source[key]) result[key] = space(source[key]);
  if (source.vehicleCombination) result.vehicleCombination = combination(source.vehicleCombination);
  if (Array.isArray(source.items)) result.items = source.items.map(item);
  if (Array.isArray(source.orderLines)) result.orderLines = source.orderLines.map(item);
  result.constraints = constraints(source.constraints || {});
  if (source.options) {
    result.options = { ...source.options };
    if (source.options.constraints) result.options.constraints = constraints(source.options.constraints);
    if (source.options.max_stack_height != null) result.options.max_stack_height = convert(source.options.max_stack_height, 'length', source.options.max_stack_height_unit ?? 'mm', 'mm', 'options.max_stack_height');
    if (source.options.max_stack_height_unit != null) result.options.max_stack_height_unit = 'mm';
  }
  return result;
}

export { item as measurementItem, space as measurementSpace, request as measurementInput }
export function measurementRun(run: OptimizationRun): OptimizationRun {
  return { ...run, input: request(run.input) }
}
